import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider';
import {
  myCharactersAtom,
  officialCharactersAtom,
  publicCharactersAtom,
  fetchCharactersByTypeAtom,
  charactersLoadingAtom,
  charactersErrorAtom,
  selectedCharacterIdAtom,
} from '../../atoms/v3/characterAtoms';
import { isDarkAtom } from '../../atoms/shared/uiAtoms';
import { CharacterCard } from './components/CharacterCard';
import { CreateCharacterCard } from './components/CreateCharacterCard';
import { CharacterDetailModal } from './components/CharacterDetailModal';
import { Character } from '../../types/character';

interface CharacterMarketProps {
  isEmbedded?: boolean;
  onClose?: () => void;
}

/**
 * 角色卡市场页面
 * 支持嵌入模式，用于桌面端模态框显示
 */
export const CharacterMarket: React.FC<CharacterMarketProps> = ({
  isEmbedded = false,
  onClose
}) => {
  const { t } = useTranslation(['v3/character', 'common']);
  const navigate = useNavigate();
  const [isDark] = useAtom(isDarkAtom);
  const { user } = useAuth();
  
  // 状态管理
  const [myCharacters] = useAtom(myCharactersAtom);
  const [officialCharacters] = useAtom(officialCharactersAtom);
  const [publicCharacters] = useAtom(publicCharactersAtom);
  const [, fetchCharactersByType] = useAtom(fetchCharactersByTypeAtom);
  const [isLoading] = useAtom(charactersLoadingAtom);
  const [error] = useAtom(charactersErrorAtom);
  const [selectedCharacterId, setSelectedCharacterId] = useAtom(selectedCharacterIdAtom);
  
  // 详情弹窗状态
  const [selectedCharacter, setSelectedCharacter] = React.useState<Character | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);

  // 页面加载时获取数据
  useEffect(() => {
    fetchCharactersByType('my');
    fetchCharactersByType('official');
    fetchCharactersByType('public');
  }, [fetchCharactersByType]);



  // 处理角色卡点击
  const handleCharacterClick = (character: Character) => {
    setSelectedCharacter(character);
    setIsDetailModalOpen(true);
  };

  // 处理快速使用角色卡
  const handleQuickUse = (characterId: string) => {
    setSelectedCharacterId(characterId);
    // 可以添加toast提示
  };

  // 处理创建角色卡
  const handleCreateCharacter = () => {
    navigate('/v3/character-editor');
  };

  // 处理编辑角色卡
  const handleEditCharacter = (characterId: string) => {
    navigate(`/v3/character-editor/${characterId}`);
  };

  // 处理返回按钮
  const handleBack = () => {
    if (isEmbedded && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };



  return (
    <div className={isEmbedded ? "h-full flex flex-col rounded-ios-lg overflow-hidden" : `min-h-screen ${isDark ? 'bg-ios-dark-bg text-ios-dark-label' : 'bg-ios-light-bg text-ios-light-label'}`}>
      {/* 头部 - 在嵌入模式下也显示，但样式简化 */}
      <div className={isEmbedded ? "sticky top-0 z-50 flex-shrink-0 bg-white dark:bg-ios-dark-gray6 border-b border-ios-gray5 dark:border-ios-dark-gray5" : `sticky top-0 z-10 ${isDark ? 'bg-ios-dark-bg border-ios-dark-separator' : 'bg-ios-light-bg border-ios-light-separator'} border-b pt-[env(safe-area-inset-top)]`}>
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={handleBack}
            className={`p-2 rounded-full ${isDark ? 'hover:bg-ios-dark-gray5' : 'hover:bg-ios-light-gray6'} transition-colors`}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-semibold">
            {t('v3/character:characterMarket', '角色卡市场')}
          </h1>
          <div className="w-9" /> {/* 占位符保持居中 */}
        </div>
      </div>

      {/* 内容区域 */}
      <div className={isEmbedded ? "flex-1 overflow-y-auto bg-ios-gray6 dark:bg-ios-dark-gray6 px-4 py-6 space-y-8" : "px-4 py-6 space-y-8"}>
        {/* 我创建的区域 */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {t('v3/character:myCharacters', '我创建的')}
              <span className={`ml-2 text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                ({myCharacters.length})
              </span>
            </h2>
          </div>
          
          <div className="space-y-6">
            {/* 创建角色卡按钮 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <CreateCharacterCard onClick={handleCreateCharacter} />
            </div>

            {/* 私密角色卡区域 */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-medium">
                  {t('v3/character:privateCharacter', '私密角色卡')}
                </h3>
                <span className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                  ({myCharacters.filter(char => char.type === 'private').length})
                </span>
              </div>
              <p className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                {t('v3/character:privateCharacterDescription', '仅您可见，可随时编辑使用')}
              </p>

              {myCharacters.filter(char => char.type === 'private').length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myCharacters.filter(char => char.type === 'private').map((character) => (
                    <CharacterCard
                      key={character.id}
                      character={character}
                      isSelected={selectedCharacterId === character.id}
                      showActions={true}
                      onCharacterClick={handleCharacterClick}
                      onQuickUse={handleQuickUse}
                      onEdit={handleEditCharacter}
                    />
                  ))}
                </div>
              ) : (
                <div className={`text-center py-8 ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                  {t('v3/character:noPrivateCharacters', '还没有私密角色卡，创建后可以在这里管理')}
                </div>
              )}
            </div>

            {/* 公开角色卡区域 */}
            {myCharacters.filter(char => char.type === 'public').length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-medium">
                    {t('v3/character:publicCharacter', '公开角色卡')}
                  </h3>
                  <span className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                    ({myCharacters.filter(char => char.type === 'public').length})
                  </span>
                </div>
                <p className={`text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                  {t('v3/character:publicCharacterDescription', '需要审核，通过后其他用户可以使用')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myCharacters.filter(char => char.type === 'public').map((character) => (
                    <CharacterCard
                      key={character.id}
                      character={character}
                      isSelected={selectedCharacterId === character.id}
                      showActions={true}
                      onCharacterClick={handleCharacterClick}
                      onQuickUse={handleQuickUse}
                      onEdit={handleEditCharacter}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* 空状态显示 */}
          {myCharacters.length === 0 && (
            <div className={`text-center py-8 ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
              {t('v3/character:noMyCharacters', '还没有创建角色卡，点击上方按钮开始创建吧！')}
            </div>
          )}
        </section>

        {/* 官方区域 */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {t('v3/character:officialCharacters', '官方')}
              <span className={`ml-2 text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                ({officialCharacters.length})
              </span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {officialCharacters.map((character) => (
              <CharacterCard
                key={character.id}
                character={character}
                isSelected={selectedCharacterId === character.id}
                showActions={false}
                onCharacterClick={handleCharacterClick}
                onQuickUse={handleQuickUse}
              />
            ))}
          </div>
          
          {officialCharacters.length === 0 && !isLoading && (
            <div className={`text-center py-8 ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
              {t('v3/character:noOfficialCharacters', '暂无官方角色卡')}
            </div>
          )}
        </section>

        {/* 其他用户区域 */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {t('v3/character:publicCharacters', '其他用户')}
              <span className={`ml-2 text-sm ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
                ({publicCharacters.length})
              </span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {publicCharacters.map((character) => (
              <CharacterCard
                key={character.id}
                character={character}
                isSelected={selectedCharacterId === character.id}
                showActions={false}
                onCharacterClick={handleCharacterClick}
                onQuickUse={handleQuickUse}
              />
            ))}
          </div>
          
          {publicCharacters.length === 0 && !isLoading && (
            <div className={`text-center py-8 ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
              {t('v3/character:noPublicCharacters', '暂无其他用户的角色卡')}
            </div>
          )}
        </section>

        {/* 加载状态 */}
        {isLoading && (
          <div className="text-center py-8">
            <div className={`inline-block animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-ios-dark-label' : 'border-ios-light-label'}`}></div>
            <p className={`mt-2 ${isDark ? 'text-ios-dark-secondary-label' : 'text-ios-light-secondary-label'}`}>
              {t('common:loading', '加载中...')}
            </p>
          </div>
        )}

        {/* 错误状态 */}
        {error && (
          <div className={`text-center py-8 ${isDark ? 'text-ios-dark-red' : 'text-ios-light-red'}`}>
            <p>{error}</p>
            <button
              onClick={() => {
                fetchCharactersByType('my');
                fetchCharactersByType('official');
                fetchCharactersByType('public');
              }}
              className={`mt-2 px-4 py-2 rounded-lg ${isDark ? 'bg-ios-dark-gray4 hover:bg-ios-dark-gray3' : 'bg-ios-light-gray5 hover:bg-ios-light-gray4'} transition-colors`}
            >
              {t('common:retry', '重试')}
            </button>
          </div>
        )}
      </div>

      {/* 角色卡详情弹窗 */}
      {selectedCharacter && (
        <CharacterDetailModal
          character={selectedCharacter}
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedCharacter(null);
          }}
          onUse={handleQuickUse}
          onEdit={selectedCharacter.creatorId ? handleEditCharacter : undefined}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
};
